import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import Cookies from "js-cookie";
import { getPlatformLogo } from "../services/api";
import { getCurrentRestaurantId } from "../utils/auth";
import { getCurrencySymbol, setActiveCurrency } from "../utils/currency";

const hexToRgbChannels = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
    : "0 0 0";
};

const applyPrimaryColor = (color: string) => {
  document.documentElement.style.setProperty("--color-main-rgb", hexToRgbChannels(color));
};
// Define your user type (customize as per your app)
export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  phonenumber: string;
  restaurantId: string;
  bonusWallet: {
    balance: number;
    currency: string;
  };
  wallet: {
    balance: number;
    currency: string;
  };

  // add more fields if needed
}

// Define the context type
interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isAuth: boolean;
  login: (userData: User) => void;
  logout: () => void;
  taxes: [TaxesProps] | null;
  setTaxes: React.Dispatch<React.SetStateAction<[TaxesProps]>>;
  categoryCodes: [categoryCodes] | null;
  setCategoryCodes: React.Dispatch<React.SetStateAction<[categoryCodes]>>;
  squareApplicationId: string | null;
  squareLocationId: string | null;
  currency: string;
  currencySymbol: string;
  setCurrency: React.Dispatch<React.SetStateAction<string>>;
  fontFamily: string;
  setFontFamily: React.Dispatch<React.SetStateAction<string>>;
  placeholderImage: string;
  setPlaceholderImage: React.Dispatch<React.SetStateAction<string>>;
  primaryColor: string;
  setPrimaryColor: React.Dispatch<React.SetStateAction<string>>;
  categoryNameColor: string;
  setCategoryNameColor: React.Dispatch<React.SetStateAction<string>>;
  itemNameColor: string;
  setItemNameColor: React.Dispatch<React.SetStateAction<string>>;
  priceColor: string;
  setPriceColor: React.Dispatch<React.SetStateAction<string>>;
  descriptionColor: string;
  setDescriptionColor: React.Dispatch<React.SetStateAction<string>>;
  categoryTabColor: string;
  setCategoryTabColor: React.Dispatch<React.SetStateAction<string>>;
  /** Re-load theme settings if the current screen's restaurant has changed */
  syncPlatformSettings: () => void;
  CategoryTabText: string;
  setCategoryTabText: React.Dispatch<React.SetStateAction<string>>;
  CategoryTabUnselectedBg: string;
  setCategoryTabUnselectedBg: React.Dispatch<React.SetStateAction<string>>;
  CategorySelectedText: string;
  setCategorySelectedText: React.Dispatch<React.SetStateAction<string>>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}
export interface TaxesProps {
  _id: string;
  country: string;
  state: string;
  city: string;
  // US
  tax_percent?: string;
  // Canada
  gst?: string;
  pst?: string;
  category_code: string;
}
export interface categoryCodes {
  _id: string;
  code: string;
  name: string;
}
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [taxes, setTaxes] = useState<[TaxesProps]>([
    {
      _id: "",
      country: "",
      state: "",
      city: "",
      tax_percent: "",
      category_code: "",
    },
  ]);
  const [categoryCodes, setCategoryCodes] = useState<[categoryCodes]>([
    {
      _id: "",
      code: "",
      name: "",
    },
  ]);
  const [squareApplicationId, setSquareApplicationId] = useState<string | null>(
    null,
  );
  const [squareLocationId, setSquareLocationId] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("USD");
  const [fontFamily, setFontFamily] = useState<string>("Figtree");
  const [placeholderImage, setPlaceholderImage] = useState<string>("");
  const [primaryColor, setPrimaryColor] = useState<string>("#000000");
  const [categoryNameColor, setCategoryNameColor] = useState<string>("#111827");
  const [itemNameColor, setItemNameColor] = useState<string>("#000000");
  const [priceColor, setPriceColor] = useState<string>("#000000");
  const [descriptionColor, setDescriptionColor] = useState<string>("#000000");
  const [categoryTabColor, setCategoryTabColor] = useState<string>("#000000");
  const [categoryTabText, setCategoryTabText] = useState<string>("#000000");
  const [categoryTabUnselectedBg, setCategoryTabUnselectedBg] = useState<string>("#000000");
  const [categorySelectedText, setCategorySelectedText] = useState<string>("#000000");

  // Derived flag
  const isAuth = !!user;

  // Load from Cookies on mount
  useEffect(() => {
    const storedUser = Cookies.get("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
    } else {
      Cookies.remove("user");
    }
  }, [user]);

  // Theme settings (and Square credentials) are per restaurant; remember which
  // restaurant they were loaded for so we only refetch when it changes.
  const loadedRestaurantRef = useRef<string | null>(null);

  const syncPlatformSettings = useCallback(() => {
    const restaurantId = getCurrentRestaurantId() || "";
    if (loadedRestaurantRef.current === restaurantId) return;
    loadedRestaurantRef.current = restaurantId;

    const fetchSquareCredentials = async () => {
      try {
        const response = await getPlatformLogo(restaurantId || undefined);
        if (response.data.success && response.data.square) {
          setSquareApplicationId(
            response.data.square.SQUARE_APPLICATION_ID || null,
          );
          setSquareLocationId(response.data.square.SQUARE_LOCATION_ID || null);
        }
        if (response.data.currency) {
          setCurrency(response.data.currency);
        }
        if (response.data.fontFamily) {
          setFontFamily(response.data.fontFamily);
          document.documentElement.style.setProperty("--app-font", response.data.fontFamily);
        }
        if (response.data.placeholderImage !== undefined) {
          setPlaceholderImage(response.data.placeholderImage);
        }
        if (response.data.primaryColor) {
          setPrimaryColor(response.data.primaryColor);
          applyPrimaryColor(response.data.primaryColor);
        }
        if (response.data.categoryNameColor) {
          setCategoryNameColor(response.data.categoryNameColor);
          document.documentElement.style.setProperty("--color-category-name", response.data.categoryNameColor);
        }
        if (response.data.itemNameColor) {
          setItemNameColor(response.data.itemNameColor);
          document.documentElement.style.setProperty("--color-item-name", response.data.itemNameColor);
        }
        if (response.data.priceColor) {
          setPriceColor(response.data.priceColor);
          document.documentElement.style.setProperty("--color-price", response.data.priceColor);
        } else if (response.data.itemNameColor) {
          setPriceColor(response.data.itemNameColor);
          document.documentElement.style.setProperty("--color-price", response.data.itemNameColor);
        }
        if (response.data.descriptionColor) {
          setDescriptionColor(response.data.descriptionColor);
          document.documentElement.style.setProperty("--color-description", response.data.descriptionColor);
        }
        if (response.data.categoryTabColor) {
          setCategoryTabColor(response.data.categoryTabColor);
          document.documentElement.style.setProperty("--color-category-tab", response.data.categoryTabColor);
        } 


        if (response.data.categoryTabText) {
          setCategoryTabText(response.data.categoryTabText);
          document.documentElement.style.setProperty("--color-category-tab-text", response.data.categoryTabText);
        }
        if (response.data.categoryTabUnselectedBg) {
          setCategoryTabUnselectedBg(response.data.categoryTabUnselectedBg);
          document.documentElement.style.setProperty("--color-category-tab-unselected-bg", response.data.categoryTabUnselectedBg);
        }
        if (response.data.categorySelectedText) {
          setCategorySelectedText(response.data.categorySelectedText);
          document.documentElement.style.setProperty("--color-category-selected-text", response.data.categorySelectedText);
        }
      } catch (error) {
        console.error("Failed to fetch Square credentials:", error);
        // Allow a retry on the next sync.
        loadedRestaurantRef.current = null;
      }
    };
    fetchSquareCredentials();
  }, []);

  useEffect(() => {
    syncPlatformSettings();
  }, [syncPlatformSettings]);

  // Auth actions
  const login = (userData: User) => setUser(userData);
  const logout = () => {
    setUser(null);
    Cookies.remove("Logintoken");
  };

  // Set before children render so non-React helpers use the same currency.
  setActiveCurrency(currency);
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuth,
        login,
        logout,
        taxes,
        setTaxes,
        categoryCodes,
        setCategoryCodes,
        squareApplicationId,
        squareLocationId,
        currency,
        currencySymbol,
        setCurrency,
        fontFamily,
        setFontFamily,
        placeholderImage,
        setPlaceholderImage,
        primaryColor,
        setPrimaryColor,
        categoryNameColor,
        setCategoryNameColor,
        itemNameColor,
        setItemNameColor,
        priceColor,
        setPriceColor,
        descriptionColor,
        setDescriptionColor,
        categoryTabColor,
        setCategoryTabColor,
        syncPlatformSettings,
        CategoryTabText: categoryTabText,
        setCategoryTabText,
        CategoryTabUnselectedBg: categoryTabUnselectedBg,
        setCategoryTabUnselectedBg,
        CategorySelectedText: categorySelectedText,
        setCategorySelectedText,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
